import React, { useEffect, useState } from 'react';
import api from 'api';
import {
  makeStyles,
  Table,
  TableFooter,
  TableRow,
  TablePagination
} from '@material-ui/core';
import Fields from 'components/Fields';
import DropdownInput from 'components/Dropdown/Dropdown';
import LoaderSpinner from 'components/LoaderSpinner/LoaderSpinner';
import CheckboxField from 'components/Fields/Checkbox/Checkbox';
import MPSRow from '../MPSRow';
import { Button } from 'components/Base';
import { Notify } from 'utils';
import { connect } from 'react-redux';
import selectors from 'store/selectors';

const useStyles = makeStyles({
  content: {
    padding: '1rem 1rem 0'
  },
  searchContainer: {
    width: '350px'
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    fontSize: '0.8rem',
    lineHeight: '1rem',
    color: '#747474',
    marginBottom: '0.2rem'
  },
  checkboxLabel: {
    fontSize: '10px'
  },
  mpsRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0.15rem 1rem',
    '&:nth-child(odd)': {
      backgroundColor: '#f9f9f9'
    },
    '& > div:first-child': {
      lineHeight: '1.8'
    }
  },
  multiSelectContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: '1rem 0',
    gap: '1rem'
  },
  pagination: {
    color: '#747474',
    borderTop: '1px solid #eaeaea',
    '& p': {
      fontSize: '0.8rem'
    }
  },
  '@global': {
    '.MuiTableCell-root': {
      borderBottom: 'none'
    }
  }
});

interface ModelPortfolioLookupProps {
  addModelPortfolioSelection: Function;
  investments: any[];
  onFinish?: Function;
  cipCategories?: any[];
  mpsFirms?: any[];
  addingModelPortfolioSelection?: boolean;
}

const ModelPortfolioLookup = ({
  addModelPortfolioSelection,
  investments,
  mpsFirms = [],
  addingModelPortfolioSelection = false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onFinish = () => {}
}: ModelPortfolioLookupProps) => {
  const classes = useStyles();

  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [areMpsSelected, setAreMpsSelected] = useState(true);
  const [cipCategory] = useState({ name: '', value: 0 });
  const [firm, setFirm] = useState({ name: '' });
  const [mappedFirms, setMappedFirms] = useState([] as any[]);
  const [isSearchComplete, setIsSearchComplete] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [page, setPage] = useState(1);
  const [modelPortfolioSelectionCount, setModelPortfolioSelectionCount] =
    useState(0);
  const [modelPortfolioSelections, setModelPortfolioSelections] = useState(
    [] as any[]
  );

  useEffect(() => {
    setMappedFirms(
      mpsFirms.map(mpsFirm => {
        return { name: mpsFirm };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectMPs = (index: number) => {
    const mps = modelPortfolioSelections as any[];
    const isSelected = mps[index].isSelected;
    mps[index].isSelected = !isSelected;
    setModelPortfolioSelections([...mps] as any);
  };

  const isMpAlreadyAdded = (modelPortfolioSelection: any) => {
    return investments.reduce((acc: boolean, mp: any) => {
      if (mp.id === Number(modelPortfolioSelection.value)) {
        acc = true;
      }
      return acc;
    }, false);
  };

  useEffect(() => {
    if (modelPortfolioSelections.length) {
      setModelPortfolioSelections(
        modelPortfolioSelections.map((mps: any) => {
          mps.isSelected = isAllSelected;
          return mps;
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllSelected]);

  const addModelPortfolio = () => {
    addModelPortfolioSelection(
      // eslint-disable-next-line array-callback-return
      modelPortfolioSelections.filter((mps: any) => {
        const mpAlreadyAdded = isMpAlreadyAdded(mps);
        if (mps.isSelected && mpAlreadyAdded) {
          Notify.warn(`${mps.name} is already added to this investment option`);
        }
        if (mps.isSelected && !mpAlreadyAdded) {
          return mps.isSelected;
        }
      })
    );
    // setModelPortfolioSelections([]);
    // onFinish();
  };

  const getModelPortfolioSelections = () => {
    setIsSearching(true);
    setIsSearchComplete(false);

    api
      .getModelPortfolioSelections({
        page,
        search: search || undefined,
        cip: cipCategory.value || undefined,
        firm_name: firm.name || undefined
      })
      .then(({ data }) => {
        setModelPortfolioSelectionCount(data.count);
        setModelPortfolioSelections(
          data.results.map((mps: any) => {
            mps.isSelected = false;
            mps.isSaved = false;
            return mps;
          })
        );

        setIsAllSelected(false);
        setIsSearching(false);
        setIsSearchComplete(true);
      });
  };

  useEffect(() => {
    const sr = !!modelPortfolioSelections.find((mps: any) => mps.isSelected);
    setAreMpsSelected(sr);
  }, [modelPortfolioSelections]);

  useEffect(() => {
    if (page === 1) {
      getModelPortfolioSelections();
    } else {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, cipCategory, firm]);

  useEffect(() => {
    getModelPortfolioSelections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <div className={classes.content}>
      <div className={classes.searchContainer}>
        <Fields.SearchGeneric
          label="Search by Model Portfolio name"
          background="#eaeaea"
          onSearch={setSearch}
        />
      </div>
      <div className={classes.multiSelectContainer}>
        <DropdownInput
          key={mappedFirms.length}
          options={mappedFirms}
          label={'Select Investment Provider'}
          onChange={setFirm}
          objectKey={'name'}
          value={firm.name}
          search={true}
        />
        {/* <DropdownInput
          options={cipCategories}
          label={'Select Model Portfolio Group'}
          onChange={setCipCategory}
          objectKey={'name'}
          value={cipCategory.name}
          search={true}
        /> */}
      </div>

      {isSearching ? (
        <LoaderSpinner isLoading={isSearching} />
      ) : (
        <>
          {isSearchComplete ? (
            modelPortfolioSelections.length === 0 ? (
              <div
                style={{
                  padding: '0 2rem',
                  textAlign: 'center',
                  fontWeight: 600
                }}
              >
                No matching Model Portfolio Selections found.
              </div>
            ) : (
              <>
                <div className={classes.checkboxContainer}>
                  <div style={{ marginRight: '1rem' }}>
                    Select All {Math.min(40, modelPortfolioSelectionCount)}
                  </div>
                  <CheckboxField
                    meta={{
                      touched: false,
                      invalid: false,
                      error: false
                    }}
                    fullWidth={false}
                    input={{
                      value: isAllSelected,
                      onChange: () => setIsAllSelected(!isAllSelected),
                      checked: isAllSelected,
                      className: classes.checkboxLabel
                    }}
                  />
                </div>
                <div
                  style={{
                    height: '200px',
                    overflow: 'auto',
                    margin: '0 -1rem'
                  }}
                >
                  {modelPortfolioSelections.map((mps: any, index: number) => {
                    return (
                      <div
                        className={classes.mpsRow}
                        key={`${mps.name}-${index}`}
                      >
                        <MPSRow mps={mps} selectMPs={selectMPs} index={index} />
                      </div>
                    );
                  })}
                </div>
                <Table>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        className={classes.pagination}
                        count={modelPortfolioSelectionCount}
                        page={page - 1}
                        rowsPerPage={40}
                        onPageChange={(event, newPage) => {
                          setPage(newPage + 1);
                        }}
                        rowsPerPageOptions={[40]}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </>
            )
          ) : (
            <></>
          )}
        </>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '1rem'
        }}
      >
        <Button
          color="primary"
          onClick={() => onFinish()}
          isLoading={addingModelPortfolioSelection}
        >
          Cancel
        </Button>
        <Button
          style={{
            pointerEvents: addingModelPortfolioSelection ? 'none' : 'all'
          }}
          disabled={!areMpsSelected}
          onClick={addModelPortfolio}
          isLoading={addingModelPortfolioSelection}
        >
          Add Model Portfolio Selection(s)
        </Button>
      </div>
    </div>
  );
};

export default connect(
  (state: any) => ({
    cipCategories: selectors.cipSelectors.getCipCategories(state),
    mpsFirms: selectors.cipSelectors.getMpsFirms(state)
  }),
  null
)(ModelPortfolioLookup);
